import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from 'react';
import {
  DEFAULT_LANGUAGE, EMPTY_STATS, EMPTY_STREAK, HINTS_PER_DAY, INSIGHTS_PER_DAY, dayNumberFor,
  getDeviceId, isLanguage, loadProfile, loadQuotas, loadStats, localDateKey, quotasFor, saveQuotas,
  type DailyQuotas, type Language, type Profile, type StorageAdapter, type Stats,
} from '@lexicon/core';
import { stringsFor, type Strings } from '../i18n';
import { pingPlay } from '../lib/api';
import { WEB_KEYS, getPlatformStorage, pingedKey } from '../lib/storage';

export interface AppState {
  /** False until the first storage read completes. */
  ready: boolean;
  /**
   * The language being played and read. Everything below that is stored
   * per language — `profile`, `stats`, and the screens' sessions — belongs
   * to *this* language (core's `scopedKey`).
   */
  language: Language;
  profile: Profile;
  stats: Stats;
  quotas: DailyQuotas;
  deviceId: string;
  /** Empty string when the player has not named themselves. */
  playerName: string;
  /** Captured once at mount so the day cannot change mid-render. */
  today: Date;
  dayNumber: number;
}

export interface AppApi extends AppState {
  storage: StorageAdapter;
  /** The UI copy for `language`. */
  t: Strings;
  refresh: () => Promise<void>;
  /** Switch language and persist the choice. */
  setLanguage: (language: Language) => Promise<void>;
  spendHint: () => Promise<void>;
  spendInsight: () => Promise<void>;
  savePlayerName: (name: string) => Promise<void>;
}

const AppContext = createContext<AppApi | null>(null);

function defaultProfile(): Profile {
  return { totalXp: 0, streak: { ...EMPTY_STREAK } };
}

function defaultStats(): Stats {
  return { played: 0, won: 0, distribution: [...EMPTY_STATS.distribution] };
}

/**
 * Fallback id used only when the normal device-id path fails — e.g.
 * `crypto.randomUUID()` throws outside a secure context (plain `http://`
 * dev/preview, standard phone testing), or storage itself is unreadable.
 * Not persisted; best effort only, per spec §7.
 */
function fallbackDeviceId(): string {
  return `local-${Math.random().toString(36).slice(2)}`;
}

/**
 * Fires the once-a-day anonymous play ping (spec §11) if `dateKey`'s marker
 * is absent. Marker-first: the marker is persisted *before* `pingPlay` is
 * even started, so a crashy fetch implementation can never cause repeated
 * ping attempts on the same day — one lost ping is an acceptable tradeoff.
 * Never awaited by callers; never throws.
 */
async function maybePing(storage: StorageAdapter, deviceId: string, dateKey: string): Promise<void> {
  try {
    const key = pingedKey(dateKey);
    if ((await storage.get(key)) !== null) return;
    await storage.set(key, '1');
    void pingPlay(deviceId);
  } catch {
    /* best-effort only — never surfaced (spec §11) */
  }
}

export interface AppProviderProps {
  children: ReactNode;
  /**
   * Injectable for tests; defaults to the platform adapter — Capacitor
   * Preferences inside the shell, `localStorage` in a browser (spec §2).
   */
  storage?: StorageAdapter;
  /** Injectable clock for tests. */
  now?: Date;
}

export function AppProvider({ children, storage = getPlatformStorage(), now }: AppProviderProps) {
  const [today] = useState(() => now ?? new Date());
  // Two-stage boot: the stored language has to be read *before* anything
  // scoped by it, because the key a profile lives under depends on it.
  // `languageLoaded` is what gates the scoped read below, so a player who
  // last played in Dutch never sees one frame of English data.
  const [languageLoaded, setLanguageLoaded] = useState(false);
  const [state, setState] = useState<AppState>(() => ({
    ready: false,
    language: DEFAULT_LANGUAGE,
    profile: defaultProfile(),
    stats: defaultStats(),
    quotas: {
      dateKey: localDateKey(today),
      hintsLeft: HINTS_PER_DAY,
      insightsLeft: INSIGHTS_PER_DAY,
    },
    deviceId: '',
    playerName: '',
    today,
    dayNumber: dayNumberFor(today),
  }));

  const stateRef = useRef(state);
  stateRef.current = state;

  // Stage one: the language itself, which nothing else can be read without.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      let stored: string | null = null;
      try {
        stored = await storage.get(WEB_KEYS.language);
      } catch {
        // Spec §7: an unreadable setting is not a reason to fail to boot.
      }
      if (cancelled) return;
      // Anything unrecognised — a hand-edited value, or a language a newer
      // build supported and this one does not — reads as the default.
      if (isLanguage(stored)) setState((s) => ({ ...s, language: stored }));
      setLanguageLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [storage]);

  // Stage two: everything scoped by the language. Re-runs on a switch, so
  // the streak, XP and stats on screen always belong to the language whose
  // board is being played.
  useEffect(() => {
    if (!languageLoaded) return undefined;
    let cancelled = false;
    const language = stateRef.current.language;
    void (async () => {
      try {
        const [profile, stats, storedQuotas, deviceId, playerName] = await Promise.all([
          loadProfile(storage, language),
          loadStats(storage, language),
          loadQuotas(storage),
          getDeviceId(storage),
          storage.get(WEB_KEYS.playerName),
        ]);
        const quotas = quotasFor(localDateKey(today), storedQuotas);
        if (quotas !== storedQuotas) await saveQuotas(storage, quotas);
        if (cancelled) return;
        setState((s) => ({
          ...s, ready: true, profile, stats, quotas, deviceId, playerName: playerName ?? '',
        }));
        void maybePing(storage, deviceId, localDateKey(today));
      } catch {
        // Any boot read can throw — e.g. crypto.randomUUID() outside a secure
        // context, or a storage backend that rejects instead of degrading
        // itself. Spec §7: never crash, always degrade to safe defaults, so
        // the app must still become playable rather than wedge on "Loading…"
        // forever. Persisting the fallback is best-effort only; skipping it
        // here keeps boot from depending on the very storage layer that just
        // failed.
        if (cancelled) return;
        setState((s) => ({
          ...s,
          ready: true,
          profile: defaultProfile(),
          stats: defaultStats(),
          quotas: quotasFor(localDateKey(today), null),
          deviceId: fallbackDeviceId(),
          playerName: '',
        }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [storage, today, languageLoaded, state.language]);

  // I1: `today`/`dayNumber` are otherwise captured once at mount, so a tab
  // or PWA left open across local midnight would serve yesterday's puzzle
  // forever (and could double-count streak/XP once it finally does roll
  // over). Whenever the tab becomes visible again — `focus` as a belt for
  // platforms that fire it without a visibility change — re-check the wall
  // clock; if the calendar day differs from the one captured in state,
  // re-derive `today`/`dayNumber`/`quotas`. DailyScreen is keyed by
  // `dayNumber` (see DailyScreen.tsx), so this alone remounts it onto the
  // new day's puzzle. Any in-progress session for the old day simply stops
  // being "today" — it stays retrievable under its own id, untouched.
  useEffect(() => {
    let cancelled = false;
    const checkRollover = () => {
      if (document.visibilityState !== 'visible') return;
      const now = new Date();
      const newDateKey = localDateKey(now);
      if (newDateKey === localDateKey(stateRef.current.today)) return;
      void (async () => {
        let stored: DailyQuotas | null = null;
        try {
          stored = await loadQuotas(storage);
        } catch {
          stored = null;
        }
        if (cancelled) return;
        const quotas = quotasFor(newDateKey, stored);
        if (quotas !== stored) void saveQuotas(storage, quotas).catch(() => {});
        setState((s) => (localDateKey(s.today) === newDateKey
          ? s
          : { ...s, today: now, dayNumber: dayNumberFor(now), quotas }));
        void maybePing(storage, stateRef.current.deviceId, newDateKey);
      })();
    };
    document.addEventListener('visibilitychange', checkRollover);
    window.addEventListener('focus', checkRollover);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', checkRollover);
      window.removeEventListener('focus', checkRollover);
    };
  }, [storage]);

  const refresh = useCallback(async () => {
    // Read through the ref, not a captured value: a refresh triggered by a
    // game that finished just before a language switch must still write
    // back the language it was actually played in.
    const language = stateRef.current.language;
    const [profile, stats] = await Promise.all([
      loadProfile(storage, language),
      loadStats(storage, language),
    ]);
    setState((s) => (s.language === language ? { ...s, profile, stats } : s));
  }, [storage]);

  const setLanguage = useCallback(async (language: Language) => {
    if (stateRef.current.language === language) return;
    // Optimistic, and deliberately so: the scoped re-read in stage two is
    // keyed off this state, and a storage write that fails (spec §7) must
    // still switch the language for this session.
    stateRef.current = { ...stateRef.current, language };
    setState((s) => ({ ...s, language }));
    try {
      await storage.set(WEB_KEYS.language, language);
    } catch {
      /* the choice holds for this session; it just will not be remembered */
    }
  }, [storage]);

  // Serializes concurrent quota spends. The naive `read stateRef -> setState
  // -> await save` version raced: two same-tick calls (e.g. a double-tap)
  // both read hintsLeft:2 off the same stale snapshot and both persisted 1,
  // handing out a free hint. The fix reads *and* writes stateRef.current
  // synchronously — with no `await` in between — before a spend ever
  // touches storage, so a second call made before React re-renders still
  // sees the first call's decrement. The queue then serializes the actual
  // storage writes so they always land in the order the spends happened.
  const spendQueueRef = useRef<Promise<void>>(Promise.resolve());

  const spendQuota = useCallback((key: 'hintsLeft' | 'insightsLeft'): Promise<void> => {
    const current = stateRef.current.quotas;
    if (current[key] <= 0) return Promise.resolve();
    const quotas: DailyQuotas = key === 'hintsLeft'
      ? { ...current, hintsLeft: current.hintsLeft - 1 }
      : { ...current, insightsLeft: current.insightsLeft - 1 };
    stateRef.current = { ...stateRef.current, quotas };
    setState((s) => ({ ...s, quotas }));

    const task = spendQueueRef.current.then(() => saveQuotas(storage, quotas));
    // Chain off this write regardless of outcome, so one rejected save
    // can't wedge every later spend behind a permanently-broken promise.
    spendQueueRef.current = task.catch(() => {});
    return task;
  }, [storage]);

  const spendHint = useCallback(() => spendQuota('hintsLeft'), [spendQuota]);
  const spendInsight = useCallback(() => spendQuota('insightsLeft'), [spendQuota]);

  const savePlayerName = useCallback(async (name: string) => {
    const playerName = name.trim().slice(0, 20);
    setState((s) => ({ ...s, playerName }));
    await storage.set(WEB_KEYS.playerName, playerName);
  }, [storage]);

  const value = useMemo<AppApi>(
    () => ({
      ...state,
      storage,
      t: stringsFor(state.language),
      refresh,
      setLanguage,
      spendHint,
      spendInsight,
      savePlayerName,
    }),
    [state, storage, refresh, setLanguage, spendHint, spendInsight, savePlayerName],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp(): AppApi {
  const value = useContext(AppContext);
  if (!value) throw new Error('useApp must be used inside <AppProvider>');
  return value;
}

/**
 * The current language's copy.
 *
 * Falls back to English when there is no provider above it, rather than
 * throwing the way `useApp` does. That is for presentational components
 * (the sheets, the keyboard, the banners) which are unit-tested on their
 * own: copy is not state a component needs a provider to render, and
 * "renders in English outside the app" is a better failure than a crash.
 * Everything the running app mounts is inside `AppProvider` (main.tsx).
 */
export function useStrings(): Strings {
  const value = useContext(AppContext);
  return value === null ? stringsFor(DEFAULT_LANGUAGE) : value.t;
}

/**
 * The language being played — for word data rather than copy: which
 * dictionary an insight comes from, which answer pool a word is drawn
 * from. Falls back to English outside a provider for the same reason
 * `useStrings` does.
 */
export function useLanguage(): Language {
  const value = useContext(AppContext);
  return value === null ? DEFAULT_LANGUAGE : value.language;
}
